import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AirlinePolicy {
  id: string;
  airline_id: string;
  travel_class: 'economy' | 'premium_economy' | 'business' | 'first';
  carry_on_weight: string;
  carry_on_dimensions: string;
  checked_weight: string;
  checked_dimensions: string;
  personal_item_weight: string | null;
  personal_item_dimensions: string | null;
  notes: string | null;
}

export interface Airline {
  id: string;
  name: string;
  iata_code: string | null;
  icao_code: string | null;
  country: string | null;
  is_active: boolean;
  policies?: AirlinePolicy[];
}

export function useAirlineData() {
  const [airlines, setAirlines] = useState<Airline[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAirlines();
  }, []);

  const fetchAirlines = async () => {
    try {
      setLoading(true);
      
      // Fetch airlines with their current policies only
      const { data: airlinesData, error: airlinesError } = await supabase
        .from('airlines')
        .select(`
          id,
          name,
          iata_code,
          icao_code,
          country,
          is_active,
          airline_baggage_policies!inner (
            id,
            airline_id,
            travel_class,
            carry_on_weight,
            carry_on_dimensions,
            checked_weight,
            checked_dimensions,
            personal_item_weight,
            personal_item_dimensions,
            notes,
            is_current
          )
        `)
        .eq('is_active', true)
        .eq('airline_baggage_policies.is_current', true)
        .order('name');

      if (airlinesError) {
        console.error('Error fetching airlines:', airlinesError);
        setError(airlinesError.message);
        return;
      }

      // Transform the data to group policies by airline
      const groupedAirlines = airlinesData?.reduce((acc: Airline[], airline: any) => {
        const existingAirline = acc.find(a => a.id === airline.id);
        
        if (existingAirline) {
          existingAirline.policies = existingAirline.policies || [];
          existingAirline.policies.push(...airline.airline_baggage_policies);
        } else {
          acc.push({
            id: airline.id,
            name: airline.name,
            iata_code: airline.iata_code,
            icao_code: airline.icao_code,
            country: airline.country,
            is_active: airline.is_active,
            policies: airline.airline_baggage_policies
          });
        }
        
        return acc;
      }, []) || [];

      setAirlines(groupedAirlines);
      setError(null);
    } catch (err) {
      console.error('Error in fetchAirlines:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getBaggagePolicy = (airlineName: string, travelClass: string) => {
    const airline = airlines.find(a => a.name === airlineName);
    if (!airline?.policies) return null;

    const classKey = travelClass === 'economy' ? 'economy' 
                   : travelClass === 'premium-economy' ? 'premium_economy'
                   : travelClass === 'business' ? 'business'
                   : travelClass === 'first' ? 'first'
                   : 'economy';

    const policy = airline.policies.find(p => p.travel_class === classKey);
    if (!policy) return null;

    return {
      carryOn: { 
        weight: policy.carry_on_weight, 
        dimensions: policy.carry_on_dimensions 
      },
      checked: { 
        weight: policy.checked_weight, 
        dimensions: policy.checked_dimensions 
      },
      personal: { 
        weight: policy.personal_item_weight || 'No limit', 
        dimensions: policy.personal_item_dimensions || 'Small personal item' 
      }
    };
  };

  const getAirlineNames = () => {
    return airlines.map(airline => airline.name).sort();
  };

  return {
    airlines,
    loading,
    error,
    getBaggagePolicy,
    getAirlineNames,
    refetch: fetchAirlines
  };
}